import { useMemo } from 'react';
import Stack, { type StackProps } from '@liquidweb-dev/undercurrent/Stack';
import { fromApiBoolean } from '@liquidweb-dev/club-sauce';

import useDomainList from 'modules/queries/domain/useList';
import { getDomainSortPriority } from 'modules/domains/statuses';

import ListWrapper from './ListWrapper';
import Empty, { EmptyFiltered } from './Empty';
import SkeletonListItem from './SkeletonListItem';
import DomainRow from './DomainRow';

export interface DomainListFilters {
	dns?: 'hosted' | 'not-hosted';
	ssl?: 'standard' | 'wildcard' | 'none';
	registration?: 'with-liquidweb' | 'not-with-liquidweb';
	autorenew?: 'enabled' | 'disabled';
}

export interface DomainListProps extends StackProps {
	filters?: DomainListFilters;
	searchValue?: string;
	onClearFilters: () => void;
	cdnDomains: Record<string, string>;
}

const DomainList = ({
	filters,
	searchValue,
	onClearFilters,
	cdnDomains,
	...rest
}: DomainListProps) => {
	// Fetch all domains (no server-side filtering except search)
	const { data: domains, isLoading: isLoadingDomainList } = useDomainList(
		{
			page_num: 1,
			page_size: 999,
			domain_match: searchValue ? true : undefined,
			domain: searchValue || undefined,
			group_subdomains: true,
		},
		{
			select: (data) =>
				data.items.sort((a, b) => {
					const aPriority = getDomainSortPriority(a);
					const bPriority = getDomainSortPriority(b);

					// First, sort by priority
					if (aPriority !== bPriority) {
						return aPriority - bPriority;
					}

					// Then, sort by expiration date
					const aExpiration = a.dreg?.expiration;
					const bExpiration = b.dreg?.expiration;

					if (!aExpiration && !bExpiration) return 0;
					if (!aExpiration) return 1;
					if (!bExpiration) return -1;

					return aExpiration.localeCompare(bExpiration);
				}),
		},
	);

	// Client-side filtering
	const filteredDomains = useMemo(() => {
		if (!domains) return [];

		type Predicate = () => boolean;

		return domains.filter((item) => {
			const predicates: Predicate[] = [];

			const dnsArray = Array.isArray(item.dns) ? item.dns : [];
			const sslArray = Array.isArray(item.ssl) ? item.ssl : [];

			// DNS filters
			if (filters?.dns === 'hosted') {
				predicates.push(() => {
					return dnsArray.some((dns) => Boolean(dns.uniq_id));
				});
			}
			if (filters?.dns === 'not-hosted') {
				predicates.push(() => {
					return !dnsArray.some((dns) => Boolean(dns.uniq_id));
				});
			}

			// SSL filters
			if (filters?.ssl === 'none') {
				predicates.push(() => {
					return !sslArray.some((ssl) => Boolean(ssl.uniq_id));
				});
			}
			if (filters?.ssl === 'standard') {
				predicates.push(() => {
					return sslArray.some((ssl) =>
						Boolean(ssl.uniq_id && !fromApiBoolean(ssl.wildcard)),
					);
				});
			}
			if (filters?.ssl === 'wildcard') {
				predicates.push(() => {
					return sslArray.some((ssl) =>
						Boolean(ssl.uniq_id && fromApiBoolean(ssl.wildcard)),
					);
				});
			}

			// Registration filters
			if (filters?.registration === 'with-liquidweb') {
				predicates.push(() => Boolean(item.dreg?.uniq_id));
			}
			if (filters?.registration === 'not-with-liquidweb') {
				predicates.push(() => !item.dreg?.uniq_id);
			}

			// Auto-renew filters (only applies to domains with registration)
			if (filters?.autorenew === 'enabled') {
				predicates.push(() =>
					Boolean(item.dreg && fromApiBoolean(item.dreg.autorenew)),
				);
			}
			if (filters?.autorenew === 'disabled') {
				predicates.push(() =>
					Boolean(item.dreg && !fromApiBoolean(item.dreg.autorenew)),
				);
			}

			return predicates.every((predicate) => predicate());
		});
	}, [domains, filters]);

	if (isLoadingDomainList) {
		return (
			<Stack {...rest}>
				<ListWrapper>
					<SkeletonListItem />
					<SkeletonListItem />
					<SkeletonListItem />
				</ListWrapper>
			</Stack>
		);
	}

	if (!domains || domains.length === 0) {
		return (
			<Stack {...rest}>
				<Empty />
			</Stack>
		);
	}

	if (filteredDomains.length === 0) {
		return (
			<Stack {...rest}>
				<EmptyFiltered onClearFilters={onClearFilters} />
			</Stack>
		);
	}

	return (
		<Stack {...rest}>
			<ListWrapper>
				{filteredDomains.map((item) => (
					<DomainRow key={item.domain} item={item} cdnDomains={cdnDomains} />
				))}
			</ListWrapper>
		</Stack>
	);
};

export default DomainList;
